import { useEffect, useState } from "react";
import { FileText, Lock, Download } from "lucide-react";
import { api, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";

function downloadDocument(sessionId, type, label) {
    const token = (() => { try { return localStorage.getItem("cursus_physio_token"); } catch { return null; } })();
    if (!token) return toast.error("Non authentifié");
    fetch(`${API}/stagiaire/sessions/${sessionId}/documents/${type}.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
    })
        .then(async (r) => {
            if (!r.ok) {
                const err = await r.json().catch(() => ({}));
                throw new Error(err.detail || "Erreur");
            }
            return r.blob();
        })
        .then((blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${label}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success(`${label} téléchargé`);
        })
        .catch((err) => toast.error(err.message || "Erreur"));
}

export default function StagiaireDocuments() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/stagiaire/documents")
            .then((r) => setItems(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="mx-auto max-w-6xl px-6 py-10" data-testid="page-stagiaire-documents">
            <div>
                <div className="text-xs uppercase tracking-[0.25em] text-brand-orange">Mes documents</div>
                <h1 className="mt-3 font-display text-5xl font-normal tracking-tightest">Tous mes documents.</h1>
                <p className="mt-2 max-w-xl text-brand-smoke">
                    Programmes, conventions, attestations — tous vos documents au même endroit.
                </p>
            </div>

            {loading ? (
                <div className="mt-10 text-brand-smoke">Chargement…</div>
            ) : items.length === 0 ? (
                <div className="mt-10 rounded-3xl border border-dashed border-brand-line bg-white p-10 text-center text-sm text-brand-smoke">
                    Vous n'avez aucun document pour l'instant.
                </div>
            ) : (
                <div className="mt-10 space-y-6">
                    {items.map((row) => (
                        <div key={row.session_id} className="rounded-2xl border border-brand-line bg-white p-7 shadow-premium" data-testid={`docs-row-${row.formation_slug}`}>
                            <div className="flex items-baseline justify-between">
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-brand-orange">{row.session_date}</div>
                                    <h3 className="mt-1 font-display text-2xl text-brand-ink">{row.formation_name}</h3>
                                </div>
                            </div>
                            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
                                {row.documents.map((doc) => (
                                    <button
                                        key={doc.type}
                                        type="button"
                                        disabled={!doc.available}
                                        onClick={() => downloadDocument(row.session_id, doc.type, `${row.formation_slug}-${doc.type}`)}
                                        data-testid={`docs-download-${row.formation_slug}-${doc.type}`}
                                        className={`flex items-center justify-between gap-2 rounded-xl border p-3 text-left text-sm transition-colors ${
                                            doc.available
                                                ? "border-brand-line bg-white text-brand-ink hover:border-brand-orange hover:bg-brand-orange/5"
                                                : "border-dashed border-brand-line bg-[#FAFAFA] text-brand-smoke cursor-not-allowed"
                                        }`}
                                    >
                                        <span className="inline-flex items-center gap-2">
                                            <FileText className="h-4 w-4 text-brand-orange" />
                                            {doc.name}
                                        </span>
                                        {doc.available ? <Download className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
